import { Validators } from '@angular/forms';

export interface ApiResponse {
  data: {
    token?: {
      access_token: string;
      refresh_token: string;
    };
    user?: {
      name: string;
      role_id: number;
      id: number;
    };
    data?: {};
    id?: number;
  };
  errors: {};
  message: string;
  status_code: number;
}

export interface ItemProps {
  name: string;
  prop: string;
  additionalProp?: string;
  hints?: any;
  showIf?: any;
  list?: PropsList;
  form?: Form;
  groupFields?: FormStepperTableView[]; // group fields to show it in table list in form stepper
}

export interface Tab {
  name: string;
  filter?: string;
  value?: any;
  to?: string;
  queryParamsProp?: string;
}

export interface ItemPropsArray {
  [name: string]: ItemProps[];
}

interface Stepper {
  number?: number;
  name?: string;
}
interface FormStepperTableView {
  prop?: string;
  type?: string;
}
interface PropsList {
  name?: string; // for a case when you want the key passed to list translation is different than the main one
  sortable?: boolean; // Sortable Flag
  translate?: boolean; // Translate grid cell text
  clickable?: boolean; // clickable grid cell
  route?: string; // if clickable is true, where it should navigate
  displayType?: string; // determine the grid cell type/style [color, image]
  width?: number; // determine entire column width
  height?: number; // determine entire column height
  cssClass?: string; // Custom CSS class for columns
  cellCssClass?: string; // Custom CSS class for cells
  cellInnerCssClass?: string; // custom inner css class for each item displayed in list-grid-content
  prefixCssClass?: string;
  suffixCssClass?: string;
  additionalContentCssClass?: string;
  trueValue?: string;
  customValue?: string;
  falseValue?: string;
  searchable?: boolean; // add field in list's search form
  searchableInline?: boolean; // add field in list's search form
  filterFieldType?: string; // [text, email, select, color_picker, number, file_input]
  showIf?: string; // show if is flag which determine which list shown this column [both, archive, active]
  link?: string;
  hidden?: boolean;
  placeHolder?: string;
  arrayList?: ArrayList; // if displayType: 'array',
  staticText?: string;
  tooltip?: Tooltip;
  action?: Actions;
  icon?: Icon;
  listPrefix?: string; // if formFieldType is select, It defines the list prefix which filled in service.lists[prefix]
  dataUrl?: string; // if formFieldType is select, it defines the list api url which fills the service.lists[prefix]
  onChange?: boolean;
  groupBy?: GroupBy;
  dateOptions?: DateOptions;
  filterOptions?: FilterOptions;
  prefix?: Prefix[];
  suffix?: Suffix[];
  additionalContent?: Suffix[];
  listPaginate?: boolean;
  showConditions?: ShowCondition[];
  bindLabel?: string;
  bindValue?: string;
  limit?: number;
}

interface Form {
  name?: string; // for a case when you want the key passed to form translation is different than the main one
  Validators?: Validators[]; // Validators => Required
  inputValueType?: string; // [formData, base64_img] (images)
  formFieldType?:
    | 'checkbox'
    | 'colorPicker'
    | 'color_picker'
    | 'confirmPassword'
    | 'datepicker'
    | 'dateTimeRangeInput'
    | 'email'
    | 'file'
    | 'file_download_details'
    | 'file_input'
    | 'file_upload_details'
    | 'formArray'
    | 'formData'
    | 'hidden'
    | 'img'
    | 'mat-slide-toggle'
    | 'ng_added_select_multiple'
    | 'ng_select'
    | 'ng_tag_select'
    | 'custom_ng_select'
    | 'ng_select_multi_lines'
    | 'ng_select_api'
    | 'ng_select_multiple'
    | 'custom_ng_select_multiple'
    | 'number'
    | 'password'
    | 'permissions'
    | 'radio'
    | 'select'
    | 'text'
    | 'textarea'
    | 'timepicker'
    | 'button_as_checkbox'
    | 'ng_select_multiple_with_details'
    | 'text_number'
    | 'float'
    | 'toggle'
    | 'hidden'
    | 'text_arabic'
    | 'checkboxs_list'
    | 'ng_select_with_checkbox'
    | 'ng_select_multiple_with_details_drag_and_drop'
    | 'map_points_select';
  arrayForm?: ItemProps[];
  listPrefix?: string; // if formFieldType is select, It defines the list prefix which filled in service.lists[prefix]
  dataUrl?: string; // if formFieldType is select, it defines the list api url which fills the service.lists[prefix]
  placeHolder?: string; // if formFieldType is image can set a placeholder when image isn't set
  initValue?: any; // Init Value if provided then form will init with it
  cssClass?: string;
  ngSelectOptions?: NgSelectOptions;
  placeHolderText?: string; // if formFieldType is image can set a placeholder when image isn't set
  groupBy?: GroupBy;
  onChange?: boolean;
  groupLabel?: string;
  prefix?: Prefix[];
  suffix?: Suffix[];
  hint?: any;
  displayType?: string;
  icon?: FormIcon;
  dateOptions?: DateOptions;
  minValue?: {
    prop?: string;
    value?: any;
    fn?: (prop?: string) => {};
  }; // min range value for range slider
  maxValue?: {
    prop?: string;
    value?: any;
    fn?: (prop?: string) => {};
  };
  minRange?: {
    prop: string;
  };
  maxRange?: {
    prop: string;
  };
  minLength?: number;
  maxLength?: number;
  listPaginate?: boolean;
  showConditions?: FormShowCondition[];
  editDisabled?: boolean;
  hiddenEdit?: boolean;
  disabled?: boolean;
  grid?: Grid;
  stepper?: Stepper;
  noSpaces?: boolean;
  disabeldIf?: {
    key: string;
    value: any;
  };
  numberOnly?: boolean;
  map?: Map;
  appendTo?: string;
}

interface Map {
  childMap?: boolean;
  color?: string;
  marker?: boolean;
  polyline?: boolean;
  circlemarker?: boolean;
  circle?: boolean;
  rectangle?: boolean;
  polygon?: boolean;
}
interface Grid {
  gt_lg?: string; // 'screen and (min-width: 1920px)'
  lt_xl?: string; // 'screen and (max-width: 1919px)'
  lt_lg?: string; // 'screen and (max-width: 1279px)'
  lt_md?: string; // 'screen and (max-width: 959px)'
  lt_sm?: string; // 'screen and (max-width: 599px)'
}

interface ShowCondition {
  fn?: (prop?: string) => {};
  fnRow?: (row?: Row, column?: ItemProps) => {};
  fnProp?: {
    prop: string;
    fn: (prop?: string) => boolean;
  };
}

interface FormShowCondition {
  /**
   * fnProp is a function that takes an input name as a prop and callback function that takes
   * that prop as a parameter to be used in resulting boolean state to show or hide an input,
   * it also clears validations on that removed input if there was any and add them back when showing it.
   */
  fnProp?: {
    prop?: string;
    fn: (prop?: string) => boolean;
  };
}

interface FormIcon {
  name: string;
  type: string;
  size: string;
  cssClass: string;
  tooltip: {
    text: string;
    translated: boolean;
  };
}

interface Prefix {
  prop?: string;
  list?: List;
  form?: Form;
} // array of prefixes to display inside cell < is selected from another cell identifier keys

interface Suffix {
  prop?: string;
  list?: List;
  form?: Form;
} // array of suffixes to display inside cell < is selected from another cell identifier keys

interface GroupBy {
  section?: string;
  tabGroup?: {
    tabGroupName: string;
    tabName: string;
    steps?: boolean;
  };
}

export interface DateOptions {
  // date Options to be used if display type is date
  timeAgo?: boolean; // display the date as a 5 hours ago method
  timeAgoCompareTo?: string; // only display date as ago if the compare function returns true, // root.service > dateAgoChecker
  displayDateAs?: string;
  min?: {
    prop?: string;
    staticValue?: string;
  };
  max?: {
    prop?: string;
    staticValue?: string;
  };
}

export interface FilterOptions {
  initValue?: any; // Init Value if provided then filter will init with it
  minRange?: {
    prop: string;
  }; // min range value for range slider
  maxRange?: {
    prop: string;
  }; // max range value for range slider
  stepRange?: any; // steps per move for range slider
  prefixUnit?: string; // steps per move for range slider
  prefix?: FilterOptionsList[];
  suffix?: FilterOptionsList[];
}

export interface NgSelectOptions {
  prefix?: NgSelectOptionsList[];
  suffix?: NgSelectOptionsList[];
}

export interface FilterOptionsList {
  prop?: string;
  displayType?: string;
  cssClass?: string;
  placeHolder?: string;
}

export interface NgSelectOptionsList {
  prop?: string;
  displayType?: string;
  cssClass?: string;
  placeHolder?: string;
}

export interface ArrayList {
  arrayOfType?: string; // string or object type of object inside array
  arrayLimit?: number; // how many items to display from array
  arrayItemDisplayType?: string; // how to display items inside array ex image, text
  placeHolder?: string; // if arrayItemDisplayType is image use this image placeholder
  prop?: string; // if arrayType object use this to get a nested obj data
  tooltip?: Tooltip; // if you want tooltip on each of the items on array,
  trimBy?: {
    prop?: string;
    list?: List;
  };
}

export interface Tooltip {
  prop?: string;
  text?: string;
  translated?: boolean;
  cssClass?: string;
  tippy?: boolean;
  displayType?: string;
  arrayList?: ArrayList;
  title?: {
    prop?: string;
    text?: string;
    translated?: boolean;
  };
}

export interface Actions {
  name?: string;
  prop?: string;
  fn?: (prop?: string) => void;
  fnRow?: (row?: Row, column?: ItemProps) => void;
}

export interface Icon {
  name?: string;
  type?: string;
  size?: string;
  style?: string;
  beforeText?: boolean; //if you want to show icon before text
  afterText?: boolean; //if you want to show icon after text
  trueIcon?: string;
  tooltip?: Tooltip;
  cssClass?: string;
}

export interface List {
  name?: string; // for a case when you want the key passed to list translation is different than the main one
  displayType?: string;
  width?: number; // determine entire column width
  link?: string;
  arrayList?: ArrayList; // if displayType: 'array',
  staticText?: string;
  action?: Actions;
  icon?: Icon;
  trueValue?: string;
  falseValue?: string;
  translate?: boolean; // Translate grid cell text
  cellCssClass?: string; // custom css class for each prefix
  cellInnerCssClass?: string; // custom inner css class for each item displayed in list-grid-content
  placeHolder?: string;
  tooltip?: Tooltip;
  dateOptions?: DateOptions;
  filterOptions?: FilterOptions;
  fn?: () => any;
  showConditions?: ShowCondition[];
}

export interface ListSummary {
  totalCostPerVolume: number;
  totalFuelCost: number;
  totalVolume: number;
}

export interface Row {
  data?: any[];
}

export interface Pagination {
  last_page?: number;
  current_page?: number;
  from?: number;
  to?: number;
  perPage?: number;
  total_records?: number;
}

export interface RowData {
  last_page: number;
  current_page: number;
  from?: number;
  to?: number;
  total?: number;
  data: any;
}

export interface MenuList {
  name: string;
  filter?: {
    key?: string;
    value: string;
  };
}

export interface ListOptions {
  itemsPerPage?: number[];
  archive?: boolean;
  restore?: boolean;
  add?: boolean;
  addAction?: boolean; // to allow create/add button listing
  edit?: boolean;
  view?: boolean;
  hiddenEdit?: boolean; // to hiddin edit button in list
  hiddenDelete?: boolean; // to hiddin delete button in list
  summary?: boolean; // show list summary section for this module in listgrid
  exportList?: boolean;
  importList?: boolean;
  exportListWithOptions?: boolean;
  clone?: boolean;
  groups?: boolean;
  hoverData?: HoverData;
  selectable?: boolean;
  archivedList?: boolean;
  close?: boolean;
  reopen?: boolean;
  pagination?: boolean;
  checkFields?: CheckFields;
  activate?: boolean;
  deactivate?: boolean;
  delete?: boolean;
  downloadable?: boolean;
  actionsCssClass?: string;
  issue?: boolean;
  archivedFilter?: boolean;
  infiniteScrolling?: boolean;
  infiniteScrollingHeight?: string; // 'var(--listHeight)' use if you want page height to be set to list height or either set a percent or px or vh
  filterContainerCssClass?: string;
  deleteDisabledIf?: DisabledIf;
  selectDisabledIf?: DisabledIf;
  editDisabledIf?: DisabledIf;
  search?: boolean; // show and hide search bar in list
}
interface DisabledIf {
  key: string;
  value: any;
}

interface CheckFields {
  archiveField?: string;
  deleteField?: string;
  editField?: string;
  activateField?: string;
  deactivateField?: string;
  downloadableField?: string;
  openField?: string;
  closeField?: string;
  archivedServiceTask?: string;
}

export interface LightBoxOptions {
  src: string;
  thumb: string;
  caption?: string;
  id?: string;
}

export interface LightBoxOptions {
  src: string;
  thumb: string;
  caption?: string;
  id?: string;
}

export class DialogData {
  title: string;
  message: string;
  showOKBtn = false;
  showCancelBtn = false;
}

export class FilterObject {
  key?: string;
  value: string;
}

export interface ListParams {
  queryParams?: {
    page?: number;
    perPage?: number;
    sort?: string;
    direction?: string;
  };
}

/**
 * All Possible SystemPermissions should be listed in this interface for easier mapping,
 * all permissions are optional if it doesn't exist then user doesn't have it,
 * it could also be custom or boolean to reflect user with full permission on a module
 */
export interface SystemPermissions {
  /**
   * just a reference for module names on the api
   */
  contract?: SystemPermission | boolean;
  contractor?: SystemPermission | boolean;
  dashboard?: SystemPermission | boolean;
  device?: SystemPermission | boolean;
  district?: SystemPermission | boolean;
  entrance?: SystemPermission | boolean;
  gate?: SystemPermission | boolean;
  permission?: SystemPermission | boolean;
  role?: SystemPermission | boolean;
  unit?: SystemPermission | boolean;
  unit_type?: SystemPermission | boolean;
  user?: SystemPermission | boolean;
  waste_type?: SystemPermission | boolean;
  zone?: SystemPermission | boolean;
  admin?: boolean;
  manageUsersRoles?: boolean;

  /**
   * could accept any other new permission name
   */
  [name: string]: SystemPermission | boolean;
}

export interface SystemPermission {
  /**
   * just a reference for permission key on the api
   */
  all?: boolean;
  name?: string;
  is_selected?: boolean;
  read?: boolean;
  create?: boolean;
  update?: boolean;
  delete?: boolean;

  archive?: boolean;
  manageArchived?: boolean;

  readAttachments?: boolean;
  manageAttachments?: boolean;

  readComments?: boolean;
  manageComments?: boolean;

  /**
   * could accept any other new permission name
   */
  [name: string]: SystemPermission | boolean | string;
}

export interface UserDetails {
  id?: number;
  name?: string;
  username?: string;
  email?: string;
  mobile?: string;
  photo?: string;
  position?: string;
  role?: number;
  interface?: string;
  manageUsers?: boolean;
  status?: boolean;
}

export interface MenuItem {
  title?: string;
  url?: string;
  icon?: string;
  mdiIcon?: boolean;
  hidden?: boolean;
  exact?: boolean;
  permissionsKey?: string;
  children?: MenuItem[];
}

export interface HoverData {
  status?: boolean;
}
